// QA Service - Quality Assurance checks for audioguide content
import { aiService } from './aiService';

const GUIDE_CONSISTENCY_SYSTEM_PROMPT = `You are a quality assurance specialist for audioguide content. Analyze the following collection of descriptions from a guided tour.

Each POI has:
- SHORT: Short description (1-2 sentences)
- IN-DEPTH: Detailed in-depth description (full paragraph)

Analyze these aspects:
1. CONSISTENCY ISSUES: Tone inconsistencies, style inconsistencies, factual contradictions
2. REPETITIVE CONTENT: Repeated phrases, redundant facts, overused vocabulary
3. VERBOSITY ISSUES: Unnecessarily wordy descriptions, redundant information, overly complex sentences

Return ONLY valid JSON in this exact format (no markdown, no extra text, just raw JSON):
{
  "summary": "Brief overall assessment (1-2 sentences)",
  "noIssuesFound": false,
  "issues": [
    {
      "id": "issue_1",
      "type": "tone_inconsistency|verbosity|repetition|contradiction|other",
      "severity": "high|medium|low",
      "affectedPois": ["POI Name 1", "POI Name 2"],
      "field": "description|inDepthDescription",
      "problem": "Clear description of what's wrong",
      "currentText": "The exact problematic text from the POI",
      "suggestedText": "Your suggested replacement text",
      "explanation": "Why this change improves the content"
    }
  ]
}

If no issues found, set "noIssuesFound": true and leave "issues" as empty array.
Respond with ONLY the JSON, absolutely no additional text.`;

const MUSEUM_CRITICISM_SYSTEM_PROMPT = `You are a comprehensive content reviewer for museum audioguides. Analyze descriptions and identify ALL quality issues including criticism, negative statements, inconsistencies, and quality problems.

Each POI has:
- SHORT: Short description (1-2 sentences)
- IN-DEPTH: Detailed in-depth description (full paragraph)

Analyze these aspects:

1. CRITICISM & NEGATIVE LANGUAGE:
   - Direct criticism or complaints about the museum/venue
   - Negative comparisons
   - Disparaging language about the museum/venue
   - Critical judgments about management/curation
   - Subtle negative framing or unfavorable descriptions

2. CONSISTENCY ISSUES:
   - Tone inconsistencies (formal vs. casual, narrative vs. instructional)
   - Style inconsistencies (first-person vs. third-person, tense shifts)
   - Factual contradictions or misaligned information between descriptions

3. REPETITIVE CONTENT:
   - Repeated phrases, sentences, or concepts across multiple descriptions
   - Redundant historical facts or stories
   - Overused vocabulary or expressions

4. VERBOSITY ISSUES:
   - Unnecessarily wordy or long-winded descriptions
   - Redundant information within a single description
   - Overly complex sentences that could be simplified

Return ONLY valid JSON in this exact format (no markdown, no extra text, just raw JSON):
{
  "summary": "Brief overall assessment (1-2 sentences)",
  "noIssuesFound": false,
  "issues": [
    {
      "id": "issue_1",
      "type": "direct_criticism|negative_comparison|disparaging_language|negative_framing|tone_inconsistency|verbosity|repetition|contradiction|other",
      "severity": "high|medium|low",
      "affectedPois": ["POI Name 1", "POI Name 2"],
      "field": "description|inDepthDescription",
      "problem": "Clear description of what's wrong",
      "currentText": "The exact problematic text",
      "suggestedText": "Corrected/improved replacement text",
      "explanation": "Why this change improves the content"
    }
  ]
}

If no issues found, set "noIssuesFound": true and leave "issues" as empty array.
Respond with ONLY the JSON, absolutely no additional text.`;

export const qaService = {
  /**
   * Analyzes all guide descriptions for consistency, repetition, and verbosity
   * @param {Array} points - Array of POI objects
   * @param {string} modelId - AI model to use
   * @returns {Promise<Object>} - QA analysis results with plain text analysis
   */
  async analyzeGuideConsistency(points, modelId = 'gpt-4') {
    try {
      if (!points || points.length === 0) {
        throw new Error('No points of interest provided for analysis');
      }

      // Filter points with inDepthDescription
      const validPoints = points.filter(
        p => p.inDepthDescription && p.inDepthDescription.trim().length > 0
      );

      if (validPoints.length === 0) {
        throw new Error('No valid descriptions found for analysis');
      }

      // Create a formatted list of POIs with their descriptions
      const poiData = validPoints
        .map((p, index) => `${index + 1}. ${p.name}\n${p.inDepthDescription}`)
        .join('\n\n---\n\n');

      const messages = [
        {
          role: 'system',
          content: GUIDE_CONSISTENCY_SYSTEM_PROMPT
        },
        {
          role: 'user',
          content: `Please analyze these audioguide descriptions:\n\n${poiData}`
        }
      ];

      const response = await aiService.getCompletion(messages, modelId, {
        maxTokens: 8192,
        temperature: 0.2
      });

      // Parse JSON response
      const result = this.parseQAResponse(response);

      return {
        ...result,
        totalPois: validPoints.length,
        qaType: 'guide'
      };
    } catch (error) {
      console.error('Guide QA check failed:', error);
      throw error;
    }
  },

  /**
   * Checks guide content for criticism of museums/places
   * @param {Array} points - Array of POI objects
   * @param {string} modelId - AI model to use
   * @returns {Promise<Object>} - Detection results with plain text analysis
   */
  async detectCriticism(points, modelId = 'gpt-4') {
    try {
      if (!points || points.length === 0) {
        throw new Error('No points of interest provided for analysis');
      }

      // Filter points with inDepthDescription
      const validPoints = points.filter(
        p => p.inDepthDescription && p.inDepthDescription.trim().length > 0
      );

      if (validPoints.length === 0) {
        throw new Error('No valid descriptions found for analysis');
      }

      // Create a formatted list of POIs with their descriptions
      const poiData = validPoints
        .map((p, index) => `${index + 1}. ${p.name}\n${p.inDepthDescription}`)
        .join('\n\n---\n\n');

      const messages = [
        {
          role: 'system',
          content: MUSEUM_CRITICISM_SYSTEM_PROMPT
        },
        {
          role: 'user',
          content: `Please analyze these audioguide descriptions:\n\n${poiData}`
        }
      ];

      const response = await aiService.getCompletion(messages, modelId, {
        maxTokens: 8192,
        temperature: 0.2
      });

      // Parse JSON response
      const result = this.parseQAResponse(response);

      return {
        ...result,
        totalPois: validPoints.length,
        qaType: 'museum'
      };
    } catch (error) {
      console.error('Museum criticism check failed:', error);
      throw error;
    }
  },

  /**
   * Parse QA response from AI - handles JSON parsing with fallback
   * @param {string} response - AI response text
   * @returns {Object} - Parsed QA result
   */
  parseQAResponse(response) {
    try {
      let jsonString = response.trim();

      // Strip markdown code blocks if present
      const jsonMatch = jsonString.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (jsonMatch) {
        jsonString = jsonMatch[1].trim();
      }

      // Try to parse as JSON
      const parsed = JSON.parse(jsonString);

      // Validate structure
      if (!parsed.summary || !Array.isArray(parsed.issues)) {
        throw new Error('Invalid QA response structure');
      }

      return {
        summary: parsed.summary,
        issues: parsed.issues || [],
        noIssuesFound: parsed.noIssuesFound || false
      };
    } catch (parseError) {
      console.error('Failed to parse QA response as JSON:', parseError);
      console.log('Raw response:', response);

      // Fallback: return error result
      return {
        summary: 'Error parsing analysis results',
        issues: [],
        noIssuesFound: false,
        parseError: true,
        rawResponse: response
      };
    }
  }
};
